import { useCallback, useEffect, useMemo, useState } from 'react'

import axios from 'axios'

import { useSendCandidateNoteBulkAction } from '../contexts/CandidateService'
import { useJHireURL } from '../contexts/Domain'

interface CandidateNoteTemplate {
    EId: string
    body: string
    channel: string
    class: string
    content: string
    default: boolean
    description: string
    flags: number
    id: number
    isDefault: boolean
    languageTag: null
    locale: string
    message: string
    messageId: "SendNotes"
    multipleTemplateBody: null
    multipleTemplateSubject: null
    section: null
    subject: string
    title: string
    type: "SendNotes"
}

export type CandidateNoteTemplateWithLocale = CandidateNoteTemplate & {
    group: string
}

export interface SendNoteTemplateResponse {
    templates: {
        [key: string]: Array<CandidateNoteTemplate>
    }
}

interface GetCandidateNoteTemplatesProps {
    applications: Array<string>
}
export function useGetCandidateNoteTemplates(props: GetCandidateNoteTemplatesProps) {
    const jhireURL = useJHireURL()

    const [loading, setLoading] = useState(true)
    const [data, setData] = useState<SendNoteTemplateResponse>()
    const [templates, setTemplates] = useState<Array<CandidateNoteTemplateWithLocale>>([])
    const [error, setError] = useState('')

    useEffect(() => {
        if (props.applications && props.applications.length > 0) {
            setLoading(true)
            setError('')

            let params = ''
            for(let i = 0; i < props.applications.length; i++) {
                const application = props.applications[i]
                params += `applicationEIds%5B%5D=${application}&`
            }

            axios.get<SendNoteTemplateResponse>(`${jhireURL}/action/application/template/SendNotes?${params}`)
                .then((response) => {
                    setData(response.data)
                    const templates: Array<CandidateNoteTemplateWithLocale> = []
                    Object.keys(response.data.templates)
                        .forEach(key => response.data.templates[key]
                        .forEach(template => templates.push({ ...template, group: key })))

                    templates.sort((a, b) => -b.group.localeCompare(a.group))
                    setTemplates(templates)
                })
                .catch((err) => {
                    setError('An unknown error occurred')
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [props, props.applications])

    return useMemo(() => ({
        loading,
        error,
        data,
        templates,
    }), [
        loading,
        error,
        templates,
        data,
    ])
}

export interface SendCandidateBulkNotePayload {
    attachmentList: Array<string>
    content: string
    entityIdList: Array<string>
    includeResume: boolean
    locale: string
    subject: string
    toList: Array<string>
}

export interface SendCandidateBulkNoteResponse {
    msgInfo: number
}

export function useSendCandidateNoteRequest() {
    const sendNote = useSendCandidateNoteBulkAction()

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [response, setResponse] = useState<SendCandidateBulkNoteResponse>(null)

    const sendNoteRequest = useCallback((data: SendCandidateBulkNotePayload) => {
        setLoading(true)
        setError('')
        return new Promise(function(resolve, reject) {
            try {
                sendNote(data, function(response) {
                    setLoading(false)
                    if (response.status === 200 && response.data.msgInfo === 200) {
                        setResponse(response.data)
                        resolve(response.data)
                    } else {
                        setError('An error occurred while trying to send message')
                        reject(response.data)
                    }
                })
            } catch(err) {
                reject(err)
            }
        })
    }, [sendNote])

    return useMemo(() => ({
        loading,
        error,
        response,
        sendNoteRequest,
    }), [
        loading,
        error,
        response,
        sendNoteRequest,
    ])
}
